import { useTranslation } from "react-i18next";
import { Form, Input } from "semantic-ui-react";

import { QUESTION_ELEMENT_TEMPLATES } from "models";
import { ConfigItemsList } from "modules/QuestionsDashboard/components/EditQuestionModal/components/QuestionElements/components/QuestionElementCard/components/ElementConfiguration/components";

import type { QuestionElementConfiguration } from "modules/QuestionsDashboard/util";

type ElementConfigurationProps = {
  template: QUESTION_ELEMENT_TEMPLATES;
  configuration: QuestionElementConfiguration;
  canEdit: boolean;
  onChange: (configuration: QuestionElementConfiguration) => void;
};

const LABEL_TEMPLATES = [
  QUESTION_ELEMENT_TEMPLATES.TextField,
  QUESTION_ELEMENT_TEMPLATES.Textarea,
  QUESTION_ELEMENT_TEMPLATES.Dropdown,
  QUESTION_ELEMENT_TEMPLATES.DropdownMulti,
  QUESTION_ELEMENT_TEMPLATES.Spinner,
  QUESTION_ELEMENT_TEMPLATES.DatePicker,
  QUESTION_ELEMENT_TEMPLATES.Number,
  QUESTION_ELEMENT_TEMPLATES.TestDriveMileage,
  QUESTION_ELEMENT_TEMPLATES.Solution
];

const DEFAULT_VALUE_TEMPLATES = [QUESTION_ELEMENT_TEMPLATES.TextField, QUESTION_ELEMENT_TEMPLATES.Textarea, QUESTION_ELEMENT_TEMPLATES.Number];

const OPTIONAL_ANSWERS_TEMPLATES = [
  QUESTION_ELEMENT_TEMPLATES.TextField,
  QUESTION_ELEMENT_TEMPLATES.Tyre,
  QUESTION_ELEMENT_TEMPLATES.TruckTyre,
  QUESTION_ELEMENT_TEMPLATES.HeavyVanTyre
];

const DROPDOWN_TEMPLATES = [QUESTION_ELEMENT_TEMPLATES.Dropdown, QUESTION_ELEMENT_TEMPLATES.DropdownMulti];

const toTexts = (labelItems?: { label: string }[]) => labelItems?.map(labelItem => labelItem.label) ?? [];

const toLabelItems = (texts: string[]) => texts.map(text => ({ label: text }));

export const ElementConfiguration = ({ template, configuration, canEdit, onChange }: ElementConfigurationProps) => {
  const { t } = useTranslation();

  const updateConfiguration = (changedFields: QuestionElementConfiguration) => onChange({ ...configuration, ...changedFields });

  return (
    <div className="ElementConfiguration">
      {LABEL_TEMPLATES.includes(template) && (
        <Form.Field>
          <Input
            fluid
            label={t("v8_label", "Label")}
            disabled={!canEdit}
            value={configuration.label ?? ""}
            onChange={(_, { value }) => updateConfiguration({ label: value })}
          />
        </Form.Field>
      )}

      {DEFAULT_VALUE_TEMPLATES.includes(template) && (
        <Form.Field>
          <Input
            fluid
            label={t("v8_default_value", "Default value")}
            disabled={!canEdit}
            type={template === QUESTION_ELEMENT_TEMPLATES.Number ? "number" : "text"}
            value={configuration.default_value ?? ""}
            onChange={(_, { value }) => updateConfiguration({ default_value: value })}
          />
        </Form.Field>
      )}

      {template === QUESTION_ELEMENT_TEMPLATES.Spinner && (
        <>
          <Form.Field>
            <Input
              fluid
              label={t("v8_unit", "Unit")}
              disabled={!canEdit}
              value={configuration.unit ?? ""}
              onChange={(_, { value }) => updateConfiguration({ unit: value })}
            />
          </Form.Field>
          <Form.Field>
            <Input
              fluid
              label={t("v8_digits", "Digits")}
              disabled={!canEdit}
              type="number"
              value={configuration.digits ?? ""}
              onChange={(_, { value }) => updateConfiguration({ digits: value })}
            />
          </Form.Field>
          <Form.Field>
            <Input
              fluid
              label={t("v8_decimals", "Decimals")}
              disabled={!canEdit}
              type="number"
              value={configuration.decimals ?? ""}
              onChange={(_, { value }) => updateConfiguration({ decimals: value })}
            />
          </Form.Field>
        </>
      )}

      {OPTIONAL_ANSWERS_TEMPLATES.includes(template) && (
        <ConfigItemsList
          title={t("v8_optional_answers", "Optional answers")}
          texts={configuration.optional_answers ?? []}
          canEdit={canEdit}
          onChange={optionalAnswers => updateConfiguration({ optional_answers: optionalAnswers })}
        />
      )}

      {DROPDOWN_TEMPLATES.includes(template) && (
        <ConfigItemsList
          title={t("v8_dropdown_items", "Dropdown items")}
          texts={toTexts(configuration.items)}
          canEdit={canEdit}
          onChange={texts => updateConfiguration({ items: toLabelItems(texts) })}
        />
      )}

      {template === QUESTION_ELEMENT_TEMPLATES.Solution && (
        <>
          <ConfigItemsList
            title={t("v8_unit_items", "Unit items")}
            texts={toTexts(configuration.unit_items)}
            canEdit={canEdit}
            onChange={texts => updateConfiguration({ unit_items: toLabelItems(texts) })}
          />
          <ConfigItemsList
            title={t("v8_value_items", "Value items")}
            texts={toTexts(configuration.value_items)}
            canEdit={canEdit}
            onChange={texts => updateConfiguration({ value_items: toLabelItems(texts) })}
          />
          <ConfigItemsList
            title={t("v8_type_items", "Type items")}
            texts={toTexts(configuration.type_items)}
            canEdit={canEdit}
            onChange={texts => updateConfiguration({ type_items: toLabelItems(texts) })}
          />
        </>
      )}
    </div>
  );
};
